/* "Find your dog's fit" on the product page (snippets/product-size-finder.liquid).
 *
 * The shopper gives their dog's back, neck and chest in cm. The product's custom.dog_size_ranges
 * (the dog's measurement range per size, overlapping) decide the size: see recommend() in
 * assets/size-fit.js. Neck is collected but not used. The matching size pill is then selected in
 * the variant picker.
 *
 * Measurements are remembered in localStorage (assets/size-fit.js) so the next product page is
 * pre-filled and pre-selected. The stored shape ({ v, m: { back, neck, chest } }) is deliberately
 * plain so a future customer-account sync can read and write the same thing; `cowalkies:dog-size`
 * fires on document with the same data whenever a size is found or cleared.
 */
(() => {
  if (customElements.get('size-finder')) return;

  const SIZES = ['xs', 's', 'm', 'l'];

  // Allowed entry range per measurement (also the wheel's scale), in whole cm. Taken from each product's
  // own dog size ranges plus LIMIT_MARGIN either side (a dog a little under XS or over L). Neck has no
  // range in the data (it doesn't steer the size) and a measurement a product doesn't list falls back
  // to these plausible values for toy/miniature poodles and maltipoos (2.5-6 kg).
  const LIMIT_MARGIN = 4;
  const DEFAULT_LIMITS = { back: [18, 40], neck: [20, 34], chest: [27, 52] };

  // Mobile scroll wheel: row height must match --cw-sf-item in component-size-finder.css.
  const ITEM_H = 40;
  const MOBILE = window.matchMedia('(max-width: 749px)');

  const Fit = window.CowalkiesSizeFit; // assets/size-fit.js, loaded just before this file
  const read = Fit.read;
  const write = Fit.write;

  // Half up for positive numbers: 27.5 -> 28, 27.49 -> 27.
  const roundHalfUp = (n) => Math.floor(n + 0.5);

  const fill = (template, values) =>
    (template || '').replace(/\{(\w+)\}/g, (m, k) => (k in values ? values[k] : m));

  class SizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      try {
        this.ranges = JSON.parse(this.querySelector('[data-sf-ranges]').textContent) || {};
        // Tolerate a metafield saved as a JSON *string* of JSON.
        if (typeof this.ranges === 'string') this.ranges = JSON.parse(this.ranges);
      } catch (e) {
        this.ranges = {};
      }
      this.rule = this.dataset.rule === 'smaller' ? 'smaller' : 'larger';

      this.trigger = this.querySelector('[data-sf-trigger]');
      this.triggerText = this.querySelector('[data-sf-trigger-text]');
      this.summary = this.querySelector('[data-sf-summary]');
      this.panel = this.querySelector('[data-sf-panel]');
      this.errorEl = this.querySelector('[data-sf-error]');
      this.clearBtn = this.querySelector('[data-sf-clear]');
      this.inputs = [...this.querySelectorAll('[data-sf-input]')];
      // Wheels the shopper has set (or that were pre-filled); live results wait until all are.
      this.touched = new Set();
      this.help = this.querySelector('[data-sf-help]');

      this.inputs.forEach((input) => {
        const limit =
          Fit.limits(this.ranges, input.dataset.key, LIMIT_MARGIN) || DEFAULT_LIMITS[input.dataset.key] || [5, 150];
        input.dataset.min = limit[0];
        input.dataset.max = limit[1];
      });

      // "How to measure your dog" link: smooth-scroll to that card instead of jumping.
      const target = document.getElementById('how-to-measure');
      const link = this.help && this.help.querySelector('a');
      if (link && target) {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        });
      } else if (this.help) {
        this.help.hidden = true;
      }

      this.inputs.forEach((input) => this.buildWheel(input));
      const onViewport = () => this.syncWheels();
      if (MOBILE.addEventListener) MOBILE.addEventListener('change', onViewport);
      else MOBILE.addListener(onViewport);

      this.trigger.addEventListener('click', () => this.toggle());
      this.querySelector('[data-sf-submit]').addEventListener('click', () => this.submit());
      this.clearBtn.addEventListener('click', () => this.clear());

      this.inputs.forEach((input) => {
        // Numbers only: stop letters, minus signs and "e" before they land, and clean pasted text.
        input.addEventListener('beforeinput', (e) => {
          if (e.data && /[^0-9.,]/.test(e.data)) e.preventDefault();
        });
        input.addEventListener('input', () => {
          const clean = this.sanitize(input.value);
          if (clean !== input.value) input.value = clean;
          this.clearError();
        });
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.submit();
          }
        });
      });

      // Pre-fill and pre-select from what the shopper entered earlier.
      const saved = read();
      if (saved) {
        this.inputs.forEach((input) => {
          const v = saved[input.dataset.key];
          if (Number.isFinite(v)) {
            input.value = String(v);
            this.touched.add(input.dataset.key);
          }
        });
        const result = this.compute(this.collect(saved));
        if (result) {
          const fromUrl = new URL(window.location.href).searchParams.has('variant');
          customElements.whenDefined('variant-selects').then(() => {
            this.show(result, { select: !fromUrl, animate: false });
            this.syncWheels();
          });
        }
      }
    }

    /* Mobile only (CSS shows these instead of the text field): an iOS-style scroll wheel per field.
       The hidden text input stays the single source of truth, so submit/validation is shared. */
    buildWheel(input) {
      const min = Number(input.dataset.min);
      const max = Number(input.dataset.max);
      const wheel = document.createElement('div');
      wheel.className = 'cw-sf__wheel';
      wheel.dataset.unit = this.dataset.tUnit;
      wheel.innerHTML = '<div class="cw-sf__wheel-band" aria-hidden="true"></div>';
      const list = document.createElement('div');
      list.className = 'cw-sf__wheel-list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', input.dataset.label);
      list.tabIndex = 0;
      const count = max - min + 1;
      for (let v = min; v <= max; v++) {
        const item = document.createElement('div');
        item.className = 'cw-sf__wheel-item';
        item.setAttribute('role', 'option');
        item.textContent = String(v);
        list.appendChild(item);
      }
      wheel.appendChild(list);
      input.closest('.cw-sf__field').appendChild(wheel);

      // An empty field shows the minimum; a saved value outside the range is clamped to its edge.
      const toIdx = (raw) => {
        const v = Math.round(parseFloat(raw));
        return Number.isFinite(v) ? Math.max(0, Math.min(count - 1, v - min)) : 0;
      };
      const w = { list, wheel, toIdx, locked: 0, silent: false, timer: 0 };
      const currentIdx = () => Math.max(0, Math.min(count - 1, Math.round(list.scrollTop / ITEM_H)));

      let frame = 0;
      const paint = () => {
        frame = 0;
        const idx = currentIdx();
        [...list.children].forEach((item, i) => {
          item.dataset.d = Math.min(3, Math.abs(i - idx));
          item.setAttribute('aria-selected', String(i === idx));
        });
        // Live value while scrolling. An empty field sitting on the minimum, or a clamped saved value,
        // is left alone; the settle step below writes the final number.
        if (toIdx(input.value) !== idx) {
          input.value = String(min + idx);
          this.clearError();
        }
      };

      // Neighbouring values show only while the wheel moves; when it stops the chosen number locks in.
      const settle = () => {
        w.timer = 0;
        wheel.classList.remove('is-scrolling');
        if (w.silent) {
          w.silent = false;
          w.locked = currentIdx();
          return;
        }
        this.engaged = true;
        const idx = currentIdx();
        if (idx === w.locked && this.touched.has(input.dataset.key)) {
          this.scheduleFold(); // let go without changing anything: still fold back
          return;
        }
        w.locked = idx;
        input.value = String(min + idx);
        this.touched.add(input.dataset.key);
        wheel.classList.remove('is-locked');
        void wheel.offsetWidth;
        wheel.classList.add('is-locked');
        this.liveApply();
      };

      // A plain tap on a wheel confirms the number it shows (needed to keep the minimum, which can't
      // be reached by scrolling).
      list.addEventListener('click', () => {
        if (this.touched.has(input.dataset.key)) return;
        const idx = currentIdx();
        w.locked = idx;
        input.value = String(min + idx);
        this.touched.add(input.dataset.key);
        this.engaged = true;
        wheel.classList.remove('is-locked');
        void wheel.offsetWidth;
        wheel.classList.add('is-locked');
        this.liveApply();
      });
      ['touchstart', 'pointerdown'].forEach((type) =>
        list.addEventListener(type, () => {
          this.pressing = true;
          this.engaged = true;
          clearTimeout(this.foldTimer);
        }, { passive: true })
      );
      ['touchend', 'touchcancel', 'pointerup', 'pointercancel'].forEach((type) =>
        list.addEventListener(type, () => {
          this.pressing = false;
          this.scheduleFold();
        }, { passive: true })
      );
      list.addEventListener(
        'wheel',
        () => {
          this.engaged = true;
          this.scheduleFold();
        },
        { passive: true }
      );
      list.addEventListener('scroll', () => {
        this.scheduleFold();
        if (!frame) frame = requestAnimationFrame(paint);
        wheel.classList.add('is-scrolling');
        clearTimeout(w.timer);
        w.timer = setTimeout(settle, 140);
      });
      w.paint = paint;
      input._wheel = w;
    }

    /* Put every wheel on the value its text field holds (after open, resize, clear, prefill). */
    syncWheels() {
      if (!MOBILE.matches) return;
      this.inputs.forEach((input) => {
        const w = input._wheel;
        if (!w) return;
        const target = w.toIdx(input.value) * ITEM_H;
        // Only mute the settle step if this really moves the wheel, otherwise no scroll event
        // would arrive to clear the flag and the shopper's first real gesture would be swallowed.
        if (Math.abs(w.list.scrollTop - target) > 1) w.silent = true;
        w.list.scrollTop = target;
        w.locked = w.toIdx(input.value);
        w.paint();
      });
    }

    /* Digits plus at most one decimal separator, one decimal place, three integer digits. */
    sanitize(raw) {
      let s = raw.replace(',', '.').replace(/[^0-9.]/g, '');
      const dot = s.indexOf('.');
      if (dot > -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
      const [int, dec] = s.split('.');
      return dec === undefined ? int.slice(0, 3) : `${int.slice(0, 3)}.${dec.slice(0, 1)}`;
    }

    toggle(force) {
      const open = typeof force === 'boolean' ? force : !this.classList.contains('is-open');
      this.classList.toggle('is-open', open);
      this.engaged = false;
      clearTimeout(this.foldTimer);
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open) this.syncWheels();
      if (open && !MOBILE.matches && window.matchMedia('(hover: hover)').matches) {
        const target = this.inputs.find((i) => !i.value) || this.inputs[0];
        // After the unfold starts, so the browser doesn't scroll to a still-collapsed field.
        setTimeout(() => target && target.focus({ preventScroll: true }), 120);
      }
    }

    showError(message, input) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.inputs.forEach((i) => i.removeAttribute('aria-invalid'));
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.focus({ preventScroll: true });
      }
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.inputs.forEach((i) => i.removeAttribute('aria-invalid'));
    }

    /* Rounded integer cm per row key, skipping empty fields. */
    collect(source) {
      const out = {};
      this.inputs.forEach((input) => {
        const raw = source ? source[input.dataset.key] : parseFloat(input.value);
        const n = Number(raw);
        if (raw !== undefined && raw !== '' && Number.isFinite(n)) out[input.dataset.key] = roundHalfUp(n);
      });
      return out;
    }

    /* Wheels (mobile) apply on their own as soon as one has been set. Wheels the shopper never touched
       stay empty and are simply left out of the calculation. */
    liveApply() {
      if (!MOBILE.matches || !this.touched.size) return;
      if (!this.submit({ auto: true })) return;
      // Fold back by itself once the shopper has let go: quickly when all three are set, after a longer
      // pause while others may still be coming. Any new touch on a wheel cancels it.
      this.scheduleFold();
    }

    /* Idle watchdog for the mobile panel: every bit of wheel activity restarts it, and it folds the
       panel once a size has been applied and nothing is pressing or still coasting. It deliberately
       doesn't depend on any one scroll/settle event arriving (flicks to the first or last value, or
       iOS rubber-banding, don't always produce the usual sequence). */
    scheduleFold() {
      clearTimeout(this.foldTimer);
      // `engaged`: the shopper actually used a wheel since the panel opened (programmatic scrolling
      // when it opens must not fold it again).
      if (!MOBILE.matches || !this.touched.size || this.pressing || !this.engaged) return;
      const all = this.touched.size >= this.inputs.length;
      const fold = () => {
        if (this.pressing || this.querySelector('.is-scrolling')) this.foldTimer = setTimeout(fold, 400);
        else this.toggle(false);
      };
      this.foldTimer = setTimeout(fold, all ? 1100 : 2400);
    }

    submit({ auto = false } = {}) {
      this.clearError();
      const filled = this.inputs.filter((i) => i.value !== '' && i.value !== '.');
      if (!filled.length) {
        this.showError(this.dataset.tErrorEmpty, this.inputs[0]);
        return;
      }
      for (const input of filled) {
        const n = roundHalfUp(parseFloat(input.value));
        const min = Number(input.dataset.min);
        const max = Number(input.dataset.max);
        if (!(n >= min && n <= max)) {
          this.showError(fill(this.dataset.tErrorRange, { min, max }), input);
          return;
        }
      }
      const measurements = this.collect();
      const result = this.compute(measurements);
      if (!result) {
        // Only neck (which doesn't steer the size) was given: ask for back or chest. On the mobile
        // wheels this just waits, there is nothing to complain about yet.
        if (!auto) this.showError(this.dataset.tErrorSteer, this.inputs[0]);
        return false;
      }
      write(measurements);
      this.show(result, { select: true, animate: true });
      if (auto) return true;
      this.toggle(false);
      this.trigger.focus({ preventScroll: true });
      return true;
    }

    /* measurements: { back: 27, ... } -> { size, between, over, measurements } or null */
    compute(measurements) {
      // Neck is collected but has no range in the data, so only back and chest steer the result.
      const result = Fit.recommend(this.ranges, this.rule, measurements);
      return result && { ...result, measurements };
    }

    /* Find the size in the variant picker (radio pills, or a dropdown) and select it. */
    selectSize(size) {
      const root = this.closest('product-info') || document;
      const wanted = size.toLowerCase();
      const radio = [...root.querySelectorAll('variant-selects input[type="radio"]')].find(
        (r) => r.value.trim().toLowerCase() === wanted
      );
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
      for (const select of root.querySelectorAll('variant-selects select')) {
        const option = [...select.options].find((o) => o.value.trim().toLowerCase() === wanted);
        if (option) {
          if (select.value !== option.value) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return true;
        }
      }
      return false;
    }

    show(result, { select, animate }) {
      const root = this.closest('product-info');
      const found = select ? this.selectSize(result.size) : true;
      const offered = found && this.hasSize(result.size);

      this.dataset.state = 'fit';
      if (root && offered) root.setAttribute('data-cw-fit', result.size);
      else if (root) root.removeAttribute('data-cw-fit');

      const lines = [];
      this.inputs.forEach((input) => {
        const v = result.measurements[input.dataset.key];
        if (Number.isFinite(v)) lines.push(`${input.dataset.label}: ${v} ${this.dataset.tUnit}`);
      });

      // Outside our sizes altogether: a short bold line instead of the usual note.
      const strong = offered && (result.over || result.under);
      const note = !offered
        ? fill(this.dataset.tUnavailable, { size: result.size })
        : result.over
          ? this.dataset.tOutOfRange
          : result.under
            ? this.dataset.tOutOfRangeSmall
            : result.between
            ? this.rule === 'smaller'
              ? this.dataset.tBetweenSmaller
              : this.dataset.tBetweenLarger
            : '';

      this.triggerText.textContent = fill(this.dataset.tTriggerFit, { size: result.size });
      this.summary.textContent = '';
      const lead = document.createElement('span');
      lead.textContent = select ? this.dataset.tSummary : this.dataset.tSummaryUrl;
      // The bold measurements are a button: tapping them reopens the wheels / fields to change anything.
      const values = document.createElement('button');
      values.type = 'button';
      values.className = 'cw-sf__summary-values';
      values.setAttribute('aria-label', this.dataset.tEdit);
      values.title = this.dataset.tEdit;
      lines.forEach((text) => {
        const item = document.createElement('span');
        const label = document.createElement('span');
        label.className = 'cw-sf__summary-item';
        label.textContent = text;
        item.append(label);
        values.append(item);
      });
      // The pencil rides inside the last item so it can never wrap onto a line of its own.
      const icon = document.createElement('span');
      icon.innerHTML =
        '<svg class="cw-sf__edit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z"/></svg>';
      values.lastElementChild.append(icon.firstChild);
      values.addEventListener('click', () => this.toggle(true));
      this.summary.append(lead, values);
      if (note) {
        const n = document.createElement('span');
        n.className = 'cw-sf__summary-note';
        if (strong) {
          const b = document.createElement('strong');
          b.textContent = note;
          n.append(b);
        } else {
          n.textContent = note;
        }
        this.summary.append(n);
      }
      this.clearBtn.hidden = false;

      if (animate) {
        [this.triggerText, this.summary].forEach((el) => {
          el.classList.remove('is-swapping');
          void el.offsetWidth;
          el.classList.add('is-swapping');
        });
      }

      document.dispatchEvent(
        new CustomEvent('cowalkies:dog-size', {
          detail: { size: result.size, measurements: result.measurements },
        })
      );
    }

    hasSize(size) {
      const root = this.closest('product-info') || document;
      const wanted = size.toLowerCase();
      return (
        [...root.querySelectorAll('variant-selects input[type="radio"]')].some(
          (r) => r.value.trim().toLowerCase() === wanted
        ) ||
        [...root.querySelectorAll('variant-selects select option')].some(
          (o) => o.value.trim().toLowerCase() === wanted
        )
      );
    }

    clear() {
      write(null);
      this.inputs.forEach((i) => (i.value = ''));
      this.syncWheels();
      this.clearError();
      this.dataset.state = 'idle';
      const root = this.closest('product-info');
      if (root) root.removeAttribute('data-cw-fit');
      this.triggerText.textContent = this.triggerText.dataset.idle;
      this.clearBtn.hidden = true;
      document.dispatchEvent(new CustomEvent('cowalkies:dog-size', { detail: { size: null, measurements: null } }));
    }
  }

  customElements.define('size-finder', SizeFinder);
})();
